import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { users, venues, devices } from '../services/api';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  CpuChipIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import SystemHealthRing from './visualizations/SystemHealthRing';
import NetworkGraph from './visualizations/NetworkGraph';
import ActivityStream from './visualizations/ActivityStream';
import AmbientBackground from './visualizations/AmbientBackground';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  venue_id?: string;
  venue_name?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
}

interface Venue {
  id: string;
  name: string;
  address?: string;
  city?: string;
  device_count: number;
  online_devices: number;
  created_at: string;
}

interface Device {
  id: string;
  device_name: string;
  venue_id: string;
  venue_name: string;
  is_online: boolean;
  last_seen?: string;
  status: string;
  active_violations: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'venues' | 'devices'>('overview');
  const [usersList, setUsersList] = useState<User[]>([]);
  const [venuesList, setVenuesList] = useState<Venue[]>([]);
  const [devicesList, setDevicesList] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalVenues: 0,
    totalDevices: 0,
    onlineDevices: 0,
    activeViolations: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all data in parallel
      const [usersRes, venuesRes, devicesRes] = await Promise.all([
        users.list(),
        venues.list(),
        devices.list()
      ]);

      setUsersList(usersRes.data || []);
      setVenuesList(venuesRes.data || []);
      setDevicesList(devicesRes.data || []);

      // Calculate stats
      const onlineDevices = devicesRes.data?.filter((d: Device) => d.is_online).length || 0;
      const activeViolations = devicesRes.data?.reduce((sum: number, d: Device) => 
        sum + (parseInt(d.active_violations?.toString() || '0') || 0), 0) || 0;

      setStats({
        totalUsers: usersRes.data?.length || 0,
        totalVenues: venuesRes.data?.length || 0,
        totalDevices: devicesRes.data?.length || 0,
        onlineDevices,
        activeViolations
      });
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await users.delete(userId);
      await loadData();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    try {
      await devices.delete(deviceId);
      await loadData();
    } catch (error) {
      console.error('Failed to delete device:', error);
    }
  };

  const renderOverview = () => {
    // Prepare data for SystemHealthRing
    const systemMetrics = [
      {
        label: 'Users',
        value: stats.totalUsers,
        maxValue: Math.max(stats.totalUsers * 1.5, 10),
        color: '#3B82F6',
        icon: <UserGroupIcon className="h-5 w-5" />,
        status: (stats.totalUsers > 0 ? 'healthy' : 'warning') as 'healthy' | 'warning' | 'critical',
      },
      {
        label: 'Venues',
        value: stats.totalVenues,
        maxValue: Math.max(stats.totalVenues * 1.5, 10),
        color: '#10B981',
        icon: <BuildingOfficeIcon className="h-5 w-5" />,
        status: (stats.totalVenues > 0 ? 'healthy' : 'warning') as 'healthy' | 'warning' | 'critical',
      },
      {
        label: 'Devices',
        value: stats.totalDevices,
        maxValue: Math.max(stats.totalDevices * 1.5, 10),
        color: '#8B5CF6',
        icon: <CpuChipIcon className="h-5 w-5" />,
        status: (stats.totalDevices > 0 ? 'healthy' : 'warning') as 'healthy' | 'warning' | 'critical',
      },
      {
        label: 'Online',
        value: stats.onlineDevices,
        maxValue: stats.totalDevices || 10,
        color: '#059669',
        icon: <SignalIcon className="h-5 w-5" />,
        status: (
          stats.totalDevices === 0 ? 'warning' :
          stats.onlineDevices / stats.totalDevices >= 0.8 ? 'healthy' :
          stats.onlineDevices / stats.totalDevices >= 0.5 ? 'warning' : 'critical'
        ) as 'healthy' | 'warning' | 'critical',
      },
      {
        label: 'Violations',
        value: stats.activeViolations,
        maxValue: Math.max(stats.activeViolations * 2, 10),
        color: '#EF4444',
        icon: <ExclamationTriangleIcon className="h-5 w-5" />,
        status: (
          stats.activeViolations === 0 ? 'healthy' :
          stats.activeViolations < 5 ? 'warning' : 'critical'
        ) as 'healthy' | 'warning' | 'critical',
      },
    ];

    // Prepare data for NetworkGraph
    const networkVenues = venuesList.map(venue => ({
      id: venue.id,
      name: venue.name,
      devices: devicesList
        .filter(d => d.venue_id === venue.id)
        .map(d => ({
          id: d.id,
          name: d.device_name,
          is_online: d.is_online,
          active_violations: d.active_violations || 0,
        })),
      online_count: venue.online_devices || 0,
    }));

    // Prepare data for ActivityStream (mock recent activities)
    const recentActivities = [
      ...usersList.slice(0, 3).map((user, i) => ({
        id: `user-${user.id}`,
        type: 'user_created' as const,
        title: 'New user registered',
        description: `${user.full_name || user.email} joined the system`,
        timestamp: user.created_at,
        metadata: {
          user_name: user.full_name || user.email,
          venue_name: user.venue_name,
        },
      })),
      ...devicesList.slice(0, 2).map((device, i) => {
        const activityType = device.is_online ? 'device_online' : 'device_offline';
        return {
          id: `device-${device.id}`,
          type: activityType as 'device_online' | 'device_offline',
          title: `Device ${device.is_online ? 'connected' : 'disconnected'}`,
          description: `${device.device_name} is now ${device.is_online ? 'online' : 'offline'}`,
          timestamp: device.last_seen || new Date().toISOString(),
          metadata: {
            device_name: device.device_name,
            venue_name: device.venue_name,
          },
        };
      }),
    ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Determine overall compliance status
    const complianceStatus: 'compliant' | 'warning' | 'violation' =
      stats.activeViolations > 0 ? 'violation' :
      stats.onlineDevices / (stats.totalDevices || 1) < 0.8 ? 'warning' :
      'compliant';

    return (
      <div className="space-y-8">
        {/* System Health Ring - Center piece */}
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <SystemHealthRing metrics={systemMetrics} size={500} />
        </motion.div>

        {/* Network Graph and Activity Stream */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <NetworkGraph venues={networkVenues} width={600} height={500} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <ActivityStream activities={recentActivities} maxItems={8} />
          </motion.div>
        </div>

        {/* Quick stats summary */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-5 gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm bg-opacity-80 border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.totalUsers}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Users</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm bg-opacity-80 border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.totalVenues}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Venues</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm bg-opacity-80 border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.totalDevices}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Devices</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm bg-opacity-80 border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{stats.onlineDevices}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Online Now</div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 backdrop-blur-sm bg-opacity-80 border border-gray-200 dark:border-gray-700">
            <div className={`text-3xl font-bold ${stats.activeViolations > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {stats.activeViolations}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Active Alerts</div>
          </div>
        </motion.div>
      </div>
    );
  };

  const renderUsers = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">All Users</h3>
        <button
          onClick={() => navigate('/users/new')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add User
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Venue</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {usersList.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{user.full_name || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{user.venue_name || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.is_active ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/users/${user.id}`)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderVenues = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">All Venues</h3>
        <button
          onClick={() => navigate('/venues/new')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Venue
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Address</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">City</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Devices</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Online</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {venuesList.map(venue => (
              <tr key={venue.id}>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">{venue.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{venue.address || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{venue.city || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{venue.device_count || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-green-600 dark:text-green-400">{venue.online_devices || 0}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => navigate(`/venues/${venue.id}`)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDevices = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">All Devices</h3>
        <button
          onClick={() => navigate('/devices/new')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Device
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Venue</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Violations</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Seen</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {devicesList.map(device => (
              <tr key={device.id}>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">{device.device_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">{device.venue_name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      device.is_online ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'
                    }`} />
                    <span className="text-gray-900 dark:text-gray-100">{device.is_online ? 'Online' : 'Offline'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {parseInt(device.active_violations?.toString() || '0') > 0 ? (
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      {device.active_violations}
                    </span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400">0</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {device.last_seen ? new Date(device.last_seen).toLocaleString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/monitoring?device=${device.id}`)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      <ChartBarIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteDevice(device.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Determine overall system status for ambient background
  const systemStatus: 'compliant' | 'warning' | 'violation' =
    stats.activeViolations > 0 ? 'violation' :
    stats.onlineDevices / (stats.totalDevices || 1) < 0.7 ? 'warning' :
    'compliant';

  return (
    <div className="relative min-h-screen">
      {/* Ambient Background */}
      <AmbientBackground
        complianceStatus={systemStatus}
        intensity={stats.activeViolations > 0 ? 0.6 : 0.3}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Mission Control
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Complete system overview and management
          </p>
        </motion.div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Users ({stats.totalUsers})
          </button>
          <button
            onClick={() => setActiveTab('venues')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'venues'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Venues ({stats.totalVenues})
          </button>
          <button
            onClick={() => setActiveTab('devices')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'devices'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Devices ({stats.totalDevices})
          </button>
        </nav>
      </div>

        {/* Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'venues' && renderVenues()}
        {activeTab === 'devices' && renderDevices()}
      </div>
    </div>
  );
};

export default AdminDashboard;